/**
 * Unified analytics service — wraps Firebase Analytics + Mixpanel.
 * Both are opt-in: if tokens/config are missing, calls are silently skipped.
 *
 * All event names MUST come from EVENTS constants in
 * `@/constants/analyticsEvents` — never use plain string literals.
 *
 * The `analytics` export is role-scoped: every role has its own namespace
 * (analytics.student.*, analytics.trainer.*, …) so call sites can only fire
 * events that belong to their surface. Generic/cross-role events live in
 * analytics.common.*.
 */
import { initFirebase, fbLogEvent, fbSetUser } from './firebase';
import {
	initMixpanel,
	mpTrack,
	mpIdentify,
	mpPeopleSet,
	mpPeopleSetOnce,
	mpPeopleIncrement,
	mpReset,
} from './mixpanel';
import { EVENTS } from '../constants/analyticsEvents';
import type { AuthUser } from '../types/auth';
import type { AuthRole } from '../types/models';

let didInit = false;

export function initAnalytics(): void {
	if (didInit) return;
	didInit = true;
	initFirebase();
	initMixpanel();
}

/**
 * Low-level track — sends to both Firebase & Mixpanel.
 * Prefer the role-scoped helpers below in call sites.
 */
export function trackEvent(
	name: string,
	properties: Record<string, unknown> = {},
): void {
	fbLogEvent(name, properties);
	mpTrack(name, properties);
}

/**
 * Identify a user on login. Pulls every relevant field off the `AuthUser`
 * object already in hand — **no extra API calls**. Fields the backend didn't
 * include are simply skipped.
 *
 * - `mixpanel.register(...)` attaches super properties (role, user_id,
 *   school_id, alforaa_id) to every subsequent event automatically, so
 *   individual call sites don't need to pass them.
 * - `people.set_once({first_seen_at})` never overwrites on re-login.
 * - `people.increment('login_count')` bumps the counter each login.
 */
export function identifyUser(user: AuthUser, role: AuthRole): void {
	const userId = (user.id || user._id || user.alforaaId || 'unknown') as string;

	const peopleProps: Record<string, unknown> = {
		$name: user.fullName || '',
		$email: user.email || '',
		role,
		user_id: userId,
		last_login_at: new Date().toISOString(),
	};
	if (user.whatsappNumber) peopleProps.$phone = user.whatsappNumber;
	if (user.alforaaId) peopleProps.alforaa_id = user.alforaaId;
	if (user.school) peopleProps.school_id = user.school;
	if (user.schoolName) peopleProps.school_name = user.schoolName;
	// Opportunistic extras — only set when backend already included them.
	if (user.classIds) peopleProps.class_ids = user.classIds;
	if (user.grade) peopleProps.grade = user.grade;
	if (user.rollNumber) peopleProps.roll_number = user.rollNumber;

	const superProps: Record<string, unknown> = {
		role,
		user_id: userId,
	};
	if (user.school) superProps.school_id = user.school;
	if (user.alforaaId) superProps.alforaa_id = user.alforaaId;

	fbSetUser(userId, { role, email: user.email || '' });
	mpIdentify(userId, peopleProps, superProps);
	mpPeopleSetOnce({ first_seen_at: new Date().toISOString() });
	mpPeopleIncrement('login_count', 1);
}

/**
 * Opportunistic profile enrichment — call from any page that already has
 * extra data in hand (e.g. a student's class list fetched for the dashboard).
 * No extra API calls — pass only what you already have.
 */
export function updateProfile(props: Record<string, unknown>): void {
	mpPeopleSet(props);
}

export function resetAnalytics(): void {
	mpReset();
}

// ─── Role-scoped helpers ─────────────────────────────────────────────────────
// Every helper is a thin wrapper around trackEvent with an EVENTS constant.
// This keeps call sites type-safe and impossible to typo.

const track = (name: string, props: Record<string, unknown> = {}): void =>
	trackEvent(name, props);

export const analytics = {
	// ─── Public / pre-auth ─────────────────────────────────────────────────
	public: {
		landingPageViewed: () => track(EVENTS.public.LANDING_PAGE_VIEWED),
		landingRoleCardClicked: (roleSelected: string) =>
			track(EVENTS.public.LANDING_ROLE_CARD_CLICKED, {
				role_selected: roleSelected,
			}),
		landingLanguageToggleClicked: (fromLang: string, toLang: string) =>
			track(EVENTS.public.LANDING_LANGUAGE_TOGGLE_CLICKED, {
				from_lang: fromLang,
				to_lang: toLang,
			}),
		landingCtaClicked: (ctaLabel: string) =>
			track(EVENTS.public.LANDING_CTA_CLICKED, { cta_label: ctaLabel }),
	},

	// ─── Student ───────────────────────────────────────────────────────────
	student: {
		loginPageViewed: () => track(EVENTS.student.LOGIN_PAGE_VIEWED),
		loginClassCodeSubmitted: (classCode: string) =>
			track(EVENTS.student.LOGIN_CLASS_CODE_SUBMITTED, { class_code: classCode }),
		loginClassCodeFailed: (reason: string) =>
			track(EVENTS.student.LOGIN_CLASS_CODE_FAILED, { reason }),
		loginIdentityVerified: () => track(EVENTS.student.LOGIN_IDENTITY_VERIFIED),
		loginIdentityFailed: (reason: string) =>
			track(EVENTS.student.LOGIN_IDENTITY_FAILED, { reason }),
		loginPinSubmitted: () => track(EVENTS.student.LOGIN_PIN_SUBMITTED),
		loginPinFailed: (reason: string) =>
			track(EVENTS.student.LOGIN_PIN_FAILED, { reason }),
		loginFaceIdAttempted: () => track(EVENTS.student.LOGIN_FACE_ID_ATTEMPTED),
		loginFaceIdSucceeded: () => track(EVENTS.student.LOGIN_FACE_ID_SUCCEEDED),
		loginFaceIdFailed: (reason: string) =>
			track(EVENTS.student.LOGIN_FACE_ID_FAILED, { reason }),
		loginPinSetCompleted: () => track(EVENTS.student.LOGIN_PIN_SET_COMPLETED),
		loginSucceeded: (method: 'pin' | 'face') =>
			track(EVENTS.student.LOGIN_SUCCEEDED, { method }),
		logoutCompleted: () => track(EVENTS.student.LOGOUT_COMPLETED),

		dashboardViewed: () => track(EVENTS.student.DASHBOARD_VIEWED),
		dashboardCurriculumCardClicked: (
			curriculumId: string,
			curriculumTitle?: string,
		) =>
			track(EVENTS.student.DASHBOARD_CURRICULUM_CARD_CLICKED, {
				curriculum_id: curriculumId,
				curriculum_title: curriculumTitle,
			}),
		dashboardRecentSessionClicked: (sessionId: string) =>
			track(EVENTS.student.DASHBOARD_RECENT_SESSION_CLICKED, {
				session_id: sessionId,
			}),
		dashboardStatsCardClicked: (statName: string) =>
			track(EVENTS.student.DASHBOARD_STATS_CARD_CLICKED, { stat_name: statName }),

		curriculaListViewed: () => track(EVENTS.student.CURRICULA_LIST_VIEWED),
		curriculaSearchSubmitted: (query: string) =>
			track(EVENTS.student.CURRICULA_SEARCH_SUBMITTED, { query }),
		curriculaFilterChanged: (filterType: string, value: string) =>
			track(EVENTS.student.CURRICULA_FILTER_CHANGED, {
				filter_type: filterType,
				value,
			}),
		curriculaCurriculumCardClicked: (
			curriculumId: string,
			curriculumTitle?: string,
		) =>
			track(EVENTS.student.CURRICULA_CURRICULUM_CARD_CLICKED, {
				curriculum_id: curriculumId,
				curriculum_title: curriculumTitle,
			}),
		curriculumDetailViewed: (curriculumId: string) =>
			track(EVENTS.student.CURRICULUM_DETAIL_VIEWED, {
				curriculum_id: curriculumId,
			}),
		curriculumModuleExpanded: (curriculumId: string, moduleIndex: number) =>
			track(EVENTS.student.CURRICULUM_MODULE_EXPANDED, {
				curriculum_id: curriculumId,
				module_index: moduleIndex,
			}),
		curriculumSessionRowClicked: (
			curriculumId: string,
			sessionId: string,
			sessionTitle?: string,
		) =>
			track(EVENTS.student.CURRICULUM_SESSION_ROW_CLICKED, {
				curriculum_id: curriculumId,
				session_id: sessionId,
				session_title: sessionTitle,
			}),
		curriculumStartSessionClicked: (curriculumId: string, sessionId: string) =>
			track(EVENTS.student.CURRICULUM_START_SESSION_CLICKED, {
				curriculum_id: curriculumId,
				session_id: sessionId,
			}),

		sessionPageViewed: (sessionId: string) =>
			track(EVENTS.student.SESSION_PAGE_VIEWED, { session_id: sessionId }),
		sessionStarted: (
			sessionId: string,
			sessionTitle?: string,
			curriculumId?: string,
		) =>
			track(EVENTS.student.SESSION_STARTED, {
				session_id: sessionId,
				session_title: sessionTitle,
				curriculum_id: curriculumId,
			}),
		sessionVideoPlayed: (sessionId: string, stepIndex: number, videoUrl?: string) =>
			track(EVENTS.student.SESSION_VIDEO_PLAYED, {
				session_id: sessionId,
				step_index: stepIndex,
				video_url: videoUrl,
			}),
		sessionVideoPaused: (
			sessionId: string,
			stepIndex: number,
			positionSec?: number,
		) =>
			track(EVENTS.student.SESSION_VIDEO_PAUSED, {
				session_id: sessionId,
				step_index: stepIndex,
				position_sec: positionSec,
			}),
		sessionVideoCompleted: (sessionId: string, stepIndex: number) =>
			track(EVENTS.student.SESSION_VIDEO_COMPLETED, {
				session_id: sessionId,
				step_index: stepIndex,
			}),
		sessionSlideAdvanced: (
			sessionId: string,
			stepIndex: number,
			direction: 'next' | 'prev',
		) =>
			track(EVENTS.student.SESSION_SLIDE_ADVANCED, {
				session_id: sessionId,
				step_index: stepIndex,
				direction,
			}),
		sessionQuizQuestionViewed: (
			sessionId: string,
			questionIndex: number,
			stepType: string,
		) =>
			track(EVENTS.student.SESSION_QUIZ_QUESTION_VIEWED, {
				session_id: sessionId,
				question_index: questionIndex,
				step_type: stepType,
			}),
		sessionQuizAnswerSubmitted: (
			sessionId: string,
			questionIndex: number,
			isCorrect: boolean,
			stepType: string,
			selectedOption?: string | number,
		) =>
			track(EVENTS.student.SESSION_QUIZ_ANSWER_SUBMITTED, {
				session_id: sessionId,
				question_index: questionIndex,
				is_correct: isCorrect,
				step_type: stepType,
				selected_option: selectedOption,
			}),
		sessionQuizRetryClicked: (sessionId: string, questionIndex: number) =>
			track(EVENTS.student.SESSION_QUIZ_RETRY_CLICKED, {
				session_id: sessionId,
				question_index: questionIndex,
			}),
		sessionDrawingSubmitted: (sessionId: string, stepIndex: number) =>
			track(EVENTS.student.SESSION_DRAWING_SUBMITTED, {
				session_id: sessionId,
				step_index: stepIndex,
			}),
		sessionImageAnnotationSubmitted: (sessionId: string, stepIndex: number) =>
			track(EVENTS.student.SESSION_IMAGE_ANNOTATION_SUBMITTED, {
				session_id: sessionId,
				step_index: stepIndex,
			}),
		sessionMediaCaptureOpened: (sessionId: string, stepIndex: number) =>
			track(EVENTS.student.SESSION_MEDIA_CAPTURE_OPENED, {
				session_id: sessionId,
				step_index: stepIndex,
			}),
		sessionMediaCaptureSubmitted: (
			sessionId: string,
			stepIndex: number,
			mediaType: string,
		) =>
			track(EVENTS.student.SESSION_MEDIA_CAPTURE_SUBMITTED, {
				session_id: sessionId,
				step_index: stepIndex,
				media_type: mediaType,
			}),
		sessionChallengeStarted: (sessionId: string, stepIndex: number) =>
			track(EVENTS.student.SESSION_CHALLENGE_STARTED, {
				session_id: sessionId,
				step_index: stepIndex,
			}),
		sessionChallengeSubmitted: (sessionId: string, stepIndex: number) =>
			track(EVENTS.student.SESSION_CHALLENGE_SUBMITTED, {
				session_id: sessionId,
				step_index: stepIndex,
			}),
		sessionFeedbackSubmitted: (sessionId: string, rating?: number) =>
			track(EVENTS.student.SESSION_FEEDBACK_SUBMITTED, {
				session_id: sessionId,
				rating,
			}),
		sessionStepCompleted: (
			sessionId: string,
			stepType: string,
			stepIndex: number,
		) =>
			track(EVENTS.student.SESSION_STEP_COMPLETED, {
				session_id: sessionId,
				step_type: stepType,
				step_index: stepIndex,
			}),
		sessionStepSkipped: (
			sessionId: string,
			stepType: string,
			stepIndex: number,
		) =>
			track(EVENTS.student.SESSION_STEP_SKIPPED, {
				session_id: sessionId,
				step_type: stepType,
				step_index: stepIndex,
			}),
		sessionExitedEarly: (sessionId: string, lastStepIndex: number) =>
			track(EVENTS.student.SESSION_EXITED_EARLY, {
				session_id: sessionId,
				last_step_index: lastStepIndex,
			}),
		sessionCompleted: (
			sessionId: string,
			score?: number,
			durationSec?: number,
		) =>
			track(EVENTS.student.SESSION_COMPLETED, {
				session_id: sessionId,
				score,
				duration_sec: durationSec,
			}),

		profilePageViewed: () => track(EVENTS.student.PROFILE_PAGE_VIEWED),
		profileAvatarClicked: () => track(EVENTS.student.PROFILE_AVATAR_CLICKED),
		profileEditClicked: () => track(EVENTS.student.PROFILE_EDIT_CLICKED),
		profilePinChangeClicked: () =>
			track(EVENTS.student.PROFILE_PIN_CHANGE_CLICKED),
		analyticsPageViewed: () => track(EVENTS.student.ANALYTICS_PAGE_VIEWED),
		analyticsFilterChanged: (filterType: string, value: string) =>
			track(EVENTS.student.ANALYTICS_FILTER_CHANGED, {
				filter_type: filterType,
				value,
			}),
	},

	// ─── Trainer ───────────────────────────────────────────────────────────
	trainer: {
		loginPageViewed: () => track(EVENTS.trainer.LOGIN_PAGE_VIEWED),
		loginSubmitted: () => track(EVENTS.trainer.LOGIN_SUBMITTED),
		loginSucceeded: () => track(EVENTS.trainer.LOGIN_SUCCEEDED),
		loginFailed: (reason: string) =>
			track(EVENTS.trainer.LOGIN_FAILED, { reason }),
		logoutCompleted: () => track(EVENTS.trainer.LOGOUT_COMPLETED),

		dashboardViewed: () => track(EVENTS.trainer.DASHBOARD_VIEWED),
		dashboardClassCardClicked: (classId: string, className?: string) =>
			track(EVENTS.trainer.DASHBOARD_CLASS_CARD_CLICKED, {
				class_id: classId,
				class_name: className,
			}),
		dashboardStudentRowClicked: (studentId: string) =>
			track(EVENTS.trainer.DASHBOARD_STUDENT_ROW_CLICKED, {
				student_id: studentId,
			}),
		dashboardStatsCardClicked: (statName: string) =>
			track(EVENTS.trainer.DASHBOARD_STATS_CARD_CLICKED, { stat_name: statName }),

		classDetailViewed: (classId: string) =>
			track(EVENTS.trainer.CLASS_DETAIL_VIEWED, { class_id: classId }),
		classStudentRowClicked: (classId: string, studentId: string) =>
			track(EVENTS.trainer.CLASS_STUDENT_ROW_CLICKED, {
				class_id: classId,
				student_id: studentId,
			}),
		classAddStudentClicked: (classId: string) =>
			track(EVENTS.trainer.CLASS_ADD_STUDENT_CLICKED, { class_id: classId }),
		classStudentAdded: (classId: string, studentId: string) =>
			track(EVENTS.trainer.CLASS_STUDENT_ADDED, {
				class_id: classId,
				student_id: studentId,
			}),
		classStudentRemoved: (classId: string, studentId: string) =>
			track(EVENTS.trainer.CLASS_STUDENT_REMOVED, {
				class_id: classId,
				student_id: studentId,
			}),
		studentDetailViewed: (studentId: string) =>
			track(EVENTS.trainer.STUDENT_DETAIL_VIEWED, { student_id: studentId }),
		studentPinResetClicked: (studentId: string) =>
			track(EVENTS.trainer.STUDENT_PIN_RESET_CLICKED, { student_id: studentId }),
		studentPinResetConfirmed: (studentId: string) =>
			track(EVENTS.trainer.STUDENT_PIN_RESET_CONFIRMED, {
				student_id: studentId,
			}),
		studentAnalyticsViewed: (studentId: string) =>
			track(EVENTS.trainer.STUDENT_ANALYTICS_VIEWED, { student_id: studentId }),
		studentSessionReviewOpened: (studentId: string, sessionId: string) =>
			track(EVENTS.trainer.STUDENT_SESSION_REVIEW_OPENED, {
				student_id: studentId,
				session_id: sessionId,
			}),

		curriculaListViewed: () => track(EVENTS.trainer.CURRICULA_LIST_VIEWED),
		curriculaCurriculumCardClicked: (curriculumId: string) =>
			track(EVENTS.trainer.CURRICULA_CURRICULUM_CARD_CLICKED, {
				curriculum_id: curriculumId,
			}),
		curriculumDetailViewed: (curriculumId: string) =>
			track(EVENTS.trainer.CURRICULUM_DETAIL_VIEWED, {
				curriculum_id: curriculumId,
			}),
		curriculumModuleExpanded: (curriculumId: string, moduleIndex: number) =>
			track(EVENTS.trainer.CURRICULUM_MODULE_EXPANDED, {
				curriculum_id: curriculumId,
				module_index: moduleIndex,
			}),
		sessionPreviewOpened: (
			curriculumId: string,
			sessionId: string,
			sessionTitle?: string,
		) =>
			track(EVENTS.trainer.SESSION_PREVIEW_OPENED, {
				curriculum_id: curriculumId,
				session_id: sessionId,
				session_title: sessionTitle,
			}),
		sessionPreviewStepViewed: (
			curriculumId: string,
			sessionId: string,
			stepType: string,
			stepIndex: number,
		) =>
			track(EVENTS.trainer.SESSION_PREVIEW_STEP_VIEWED, {
				curriculum_id: curriculumId,
				session_id: sessionId,
				step_type: stepType,
				step_index: stepIndex,
			}),
		sessionPreviewClosed: (
			curriculumId: string,
			sessionId: string,
			lastStepIndex: number,
		) =>
			track(EVENTS.trainer.SESSION_PREVIEW_CLOSED, {
				curriculum_id: curriculumId,
				session_id: sessionId,
				last_step_index: lastStepIndex,
			}),
	},

	// ─── Parent ────────────────────────────────────────────────────────────
	parent: {
		loginPageViewed: () => track(EVENTS.parent.LOGIN_PAGE_VIEWED),
		loginMagicLinkRequested: (whatsappNumber?: string) =>
			track(EVENTS.parent.LOGIN_MAGIC_LINK_REQUESTED, {
				whatsapp_number: whatsappNumber,
			}),
		loginMagicLinkVerified: () =>
			track(EVENTS.parent.LOGIN_MAGIC_LINK_VERIFIED),
		loginPinSubmitted: () => track(EVENTS.parent.LOGIN_PIN_SUBMITTED),
		loginPinFailed: (reason: string) =>
			track(EVENTS.parent.LOGIN_PIN_FAILED, { reason }),
		loginPinSetCompleted: () => track(EVENTS.parent.LOGIN_PIN_SET_COMPLETED),
		loginSucceeded: () => track(EVENTS.parent.LOGIN_SUCCEEDED),
		logoutCompleted: () => track(EVENTS.parent.LOGOUT_COMPLETED),

		dashboardViewed: () => track(EVENTS.parent.DASHBOARD_VIEWED),
		dashboardChildSelected: (childId: string, childName?: string) =>
			track(EVENTS.parent.DASHBOARD_CHILD_SELECTED, {
				child_id: childId,
				child_name: childName,
			}),
		dashboardCurriculumCardClicked: (curriculumId: string, childId: string) =>
			track(EVENTS.parent.DASHBOARD_CURRICULUM_CARD_CLICKED, {
				curriculum_id: curriculumId,
				child_id: childId,
			}),
		dashboardSessionReviewOpened: (sessionId: string, childId: string) =>
			track(EVENTS.parent.DASHBOARD_SESSION_REVIEW_OPENED, {
				session_id: sessionId,
				child_id: childId,
			}),
		analyticsPageViewed: (childId?: string) =>
			track(EVENTS.parent.ANALYTICS_PAGE_VIEWED, { child_id: childId }),
		analyticsFilterChanged: (
			filterType: string,
			value: string,
			childId?: string,
		) =>
			track(EVENTS.parent.ANALYTICS_FILTER_CHANGED, {
				filter_type: filterType,
				value,
				child_id: childId,
			}),
	},

	// ─── School admin ──────────────────────────────────────────────────────
	schoolAdmin: {
		loginPageViewed: () => track(EVENTS.schoolAdmin.LOGIN_PAGE_VIEWED),
		loginSubmitted: () => track(EVENTS.schoolAdmin.LOGIN_SUBMITTED),
		loginSucceeded: () => track(EVENTS.schoolAdmin.LOGIN_SUCCEEDED),
		loginFailed: (reason: string) =>
			track(EVENTS.schoolAdmin.LOGIN_FAILED, { reason }),
		logoutCompleted: () => track(EVENTS.schoolAdmin.LOGOUT_COMPLETED),
		setupPageViewed: () => track(EVENTS.schoolAdmin.SETUP_PAGE_VIEWED),
		setupPasswordSubmitted: () =>
			track(EVENTS.schoolAdmin.SETUP_PASSWORD_SUBMITTED),
		setupCompleted: () => track(EVENTS.schoolAdmin.SETUP_COMPLETED),

		dashboardViewed: () => track(EVENTS.schoolAdmin.DASHBOARD_VIEWED),
		dashboardStatsCardClicked: (statName: string) =>
			track(EVENTS.schoolAdmin.DASHBOARD_STATS_CARD_CLICKED, {
				stat_name: statName,
			}),

		classesPageViewed: () => track(EVENTS.schoolAdmin.CLASSES_PAGE_VIEWED),
		classesCreateClicked: () => track(EVENTS.schoolAdmin.CLASSES_CREATE_CLICKED),
		classesCreateSubmitted: (className: string, grade?: string) =>
			track(EVENTS.schoolAdmin.CLASSES_CREATE_SUBMITTED, {
				class_name: className,
				grade,
			}),
		classesCreated: (classId: string, className: string) =>
			track(EVENTS.schoolAdmin.CLASSES_CREATED, {
				class_id: classId,
				class_name: className,
			}),
		classesRowClicked: (classId: string) =>
			track(EVENTS.schoolAdmin.CLASSES_ROW_CLICKED, { class_id: classId }),
		classesEditClicked: (classId: string) =>
			track(EVENTS.schoolAdmin.CLASSES_EDIT_CLICKED, { class_id: classId }),
		classesUpdated: (classId: string) =>
			track(EVENTS.schoolAdmin.CLASSES_UPDATED, { class_id: classId }),
		classesDeleteClicked: (classId: string) =>
			track(EVENTS.schoolAdmin.CLASSES_DELETE_CLICKED, { class_id: classId }),
		classesDeleted: (classId: string) =>
			track(EVENTS.schoolAdmin.CLASSES_DELETED, { class_id: classId }),

		trainersPageViewed: () => track(EVENTS.schoolAdmin.TRAINERS_PAGE_VIEWED),
		trainersCreateClicked: () =>
			track(EVENTS.schoolAdmin.TRAINERS_CREATE_CLICKED),
		trainersCreateSubmitted: (email: string) =>
			track(EVENTS.schoolAdmin.TRAINERS_CREATE_SUBMITTED, { email }),
		trainersCreated: (trainerId: string, email: string) =>
			track(EVENTS.schoolAdmin.TRAINERS_CREATED, {
				trainer_id: trainerId,
				email,
			}),
		trainersRowClicked: (trainerId: string) =>
			track(EVENTS.schoolAdmin.TRAINERS_ROW_CLICKED, { trainer_id: trainerId }),
		trainersClassAssigned: (trainerId: string, classId: string) =>
			track(EVENTS.schoolAdmin.TRAINERS_CLASS_ASSIGNED, {
				trainer_id: trainerId,
				class_id: classId,
			}),
		trainersDeleted: (trainerId: string) =>
			track(EVENTS.schoolAdmin.TRAINERS_DELETED, { trainer_id: trainerId }),

		studentsPageViewed: () => track(EVENTS.schoolAdmin.STUDENTS_PAGE_VIEWED),
		studentsRosterUploadOpened: () =>
			track(EVENTS.schoolAdmin.STUDENTS_ROSTER_UPLOAD_OPENED),
		studentsRosterUploadSubmitted: (fileName?: string) =>
			track(EVENTS.schoolAdmin.STUDENTS_ROSTER_UPLOAD_SUBMITTED, {
				file_name: fileName,
			}),
		studentsRosterUploadCompleted: (
			created: number,
			errors: number,
			skipped: number,
		) =>
			track(EVENTS.schoolAdmin.STUDENTS_ROSTER_UPLOAD_COMPLETED, {
				created,
				errors,
				skipped,
			}),
		studentsRowClicked: (studentId: string) =>
			track(EVENTS.schoolAdmin.STUDENTS_ROW_CLICKED, { student_id: studentId }),
		studentsClassAssigned: (studentId: string, classId: string) =>
			track(EVENTS.schoolAdmin.STUDENTS_CLASS_ASSIGNED, {
				student_id: studentId,
				class_id: classId,
			}),
		studentsDeleted: (studentId: string) =>
			track(EVENTS.schoolAdmin.STUDENTS_DELETED, { student_id: studentId }),

		analyticsPageViewed: () => track(EVENTS.schoolAdmin.ANALYTICS_PAGE_VIEWED),
		analyticsFilterChanged: (filterType: string, value: string) =>
			track(EVENTS.schoolAdmin.ANALYTICS_FILTER_CHANGED, {
				filter_type: filterType,
				value,
			}),
	},

	// ─── Master admin ──────────────────────────────────────────────────────
	masterAdmin: {
		loginPageViewed: () => track(EVENTS.masterAdmin.LOGIN_PAGE_VIEWED),
		loginCredentialsSubmitted: () =>
			track(EVENTS.masterAdmin.LOGIN_CREDENTIALS_SUBMITTED),
		loginCredentialsSucceeded: () =>
			track(EVENTS.masterAdmin.LOGIN_CREDENTIALS_SUCCEEDED),
		loginCredentialsFailed: (reason: string) =>
			track(EVENTS.masterAdmin.LOGIN_CREDENTIALS_FAILED, { reason }),
		loginTotpSubmitted: () => track(EVENTS.masterAdmin.LOGIN_TOTP_SUBMITTED),
		loginTotpSucceeded: () => track(EVENTS.masterAdmin.LOGIN_TOTP_SUCCEEDED),
		loginTotpFailed: (reason: string) =>
			track(EVENTS.masterAdmin.LOGIN_TOTP_FAILED, { reason }),
		logoutCompleted: () => track(EVENTS.masterAdmin.LOGOUT_COMPLETED),

		dashboardViewed: () => track(EVENTS.masterAdmin.DASHBOARD_VIEWED),
		dashboardStatsCardClicked: (statName: string) =>
			track(EVENTS.masterAdmin.DASHBOARD_STATS_CARD_CLICKED, {
				stat_name: statName,
			}),

		schoolsPageViewed: () => track(EVENTS.masterAdmin.SCHOOLS_PAGE_VIEWED),
		schoolsSearchSubmitted: (query: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_SEARCH_SUBMITTED, { query }),
		schoolsFilterChanged: (filterType: string, value: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_FILTER_CHANGED, {
				filter_type: filterType,
				value,
			}),
		schoolsCreateClicked: () =>
			track(EVENTS.masterAdmin.SCHOOLS_CREATE_CLICKED),
		schoolsCreateSubmitted: (schoolName: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_CREATE_SUBMITTED, {
				school_name: schoolName,
			}),
		schoolsCreated: (schoolId: string, schoolName: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_CREATED, {
				school_id: schoolId,
				school_name: schoolName,
			}),
		schoolsRowClicked: (schoolId: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_ROW_CLICKED, { school_id: schoolId }),
		schoolsEditClicked: (schoolId: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_EDIT_CLICKED, { school_id: schoolId }),
		schoolsUpdated: (schoolId: string, schoolName?: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_UPDATED, {
				school_id: schoolId,
				school_name: schoolName,
			}),
		schoolsStatusToggled: (schoolId: string, newStatus: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_STATUS_TOGGLED, {
				school_id: schoolId,
				new_status: newStatus,
			}),
		schoolsDeleteClicked: (schoolId: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_DELETE_CLICKED, { school_id: schoolId }),
		schoolsDeleted: (schoolId: string) =>
			track(EVENTS.masterAdmin.SCHOOLS_DELETED, { school_id: schoolId }),
		schoolDetailViewed: (schoolId: string) =>
			track(EVENTS.masterAdmin.SCHOOL_DETAIL_VIEWED, { school_id: schoolId }),

		usersPageViewed: () => track(EVENTS.masterAdmin.USERS_PAGE_VIEWED),
		usersFilterChanged: (roleFilter: string) =>
			track(EVENTS.masterAdmin.USERS_FILTER_CHANGED, { role_filter: roleFilter }),
		usersCreateClicked: () => track(EVENTS.masterAdmin.USERS_CREATE_CLICKED),
		usersCreateSubmitted: (role: string) =>
			track(EVENTS.masterAdmin.USERS_CREATE_SUBMITTED, { role }),
		usersCreated: (userId: string, role: string) =>
			track(EVENTS.masterAdmin.USERS_CREATED, { user_id: userId, role }),
		usersRowClicked: (userId: string, role: string) =>
			track(EVENTS.masterAdmin.USERS_ROW_CLICKED, { user_id: userId, role }),
		usersStatusToggled: (userId: string, role: string, newStatus: string) =>
			track(EVENTS.masterAdmin.USERS_STATUS_TOGGLED, {
				user_id: userId,
				role,
				new_status: newStatus,
			}),
		usersCredentialsReset: (userId: string, role: string) =>
			track(EVENTS.masterAdmin.USERS_CREDENTIALS_RESET, {
				user_id: userId,
				role,
			}),
		usersDeleted: (userId: string, role: string) =>
			track(EVENTS.masterAdmin.USERS_DELETED, { user_id: userId, role }),

		curriculumListViewed: () =>
			track(EVENTS.masterAdmin.CURRICULUM_LIST_VIEWED),
		curriculumCreateClicked: () =>
			track(EVENTS.masterAdmin.CURRICULUM_CREATE_CLICKED),
		curriculumCreated: (curriculumId: string, title: string) =>
			track(EVENTS.masterAdmin.CURRICULUM_CREATED, {
				curriculum_id: curriculumId,
				title,
			}),
		curriculumCardClicked: (curriculumId: string) =>
			track(EVENTS.masterAdmin.CURRICULUM_CARD_CLICKED, {
				curriculum_id: curriculumId,
			}),
		curriculumDetailViewed: (curriculumId: string) =>
			track(EVENTS.masterAdmin.CURRICULUM_DETAIL_VIEWED, {
				curriculum_id: curriculumId,
			}),
		curriculumUpdated: (curriculumId: string, title?: string) =>
			track(EVENTS.masterAdmin.CURRICULUM_UPDATED, {
				curriculum_id: curriculumId,
				title,
			}),
		curriculumDeleted: (curriculumId: string) =>
			track(EVENTS.masterAdmin.CURRICULUM_DELETED, {
				curriculum_id: curriculumId,
			}),
		curriculumModuleExpanded: (curriculumId: string, moduleIndex: number) =>
			track(EVENTS.masterAdmin.CURRICULUM_MODULE_EXPANDED, {
				curriculum_id: curriculumId,
				module_index: moduleIndex,
			}),
		curriculumSessionClicked: (curriculumId: string, sessionId: string) =>
			track(EVENTS.masterAdmin.CURRICULUM_SESSION_CLICKED, {
				curriculum_id: curriculumId,
				session_id: sessionId,
			}),

		analyticsPageViewed: () =>
			track(EVENTS.masterAdmin.ANALYTICS_PAGE_VIEWED),
		analyticsFilterChanged: (filterType: string, value: string) =>
			track(EVENTS.masterAdmin.ANALYTICS_FILTER_CHANGED, {
				filter_type: filterType,
				value,
			}),
		auditLogsPageViewed: () =>
			track(EVENTS.masterAdmin.AUDIT_LOGS_PAGE_VIEWED),
		auditLogsFilterChanged: (filterType: string, value: string) =>
			track(EVENTS.masterAdmin.AUDIT_LOGS_FILTER_CHANGED, {
				filter_type: filterType,
				value,
			}),
		auditLogsRowClicked: (logId: string, action?: string) =>
			track(EVENTS.masterAdmin.AUDIT_LOGS_ROW_CLICKED, {
				log_id: logId,
				action,
			}),
		configPageViewed: () => track(EVENTS.masterAdmin.CONFIG_PAGE_VIEWED),
		configFlagToggled: (flagKey: string, newValue: boolean) =>
			track(EVENTS.masterAdmin.CONFIG_FLAG_TOGGLED, {
				flag_key: flagKey,
				new_value: newValue,
			}),
		configSaved: (changedFlags?: string[]) =>
			track(EVENTS.masterAdmin.CONFIG_SAVED, { changed_flags: changedFlags }),
	},
};

export default analytics;
